/**
 * Helpers de classification du champ `transaction` d'un bien.
 *
 * Le champ accepte 2 valeurs : "location", "vente" (cf. types/bien.ts +
 * schemas/bien.ts). La 3e valeur historique "location_vente" (biens à la fois
 * en location et en vente) a été retirée le 2026-07-10 (audit process) : le
 * choix se fait désormais une fois pour toutes à la création/duplication du
 * bien (modale Location/Vente, cf. BienCreateFormPage.tsx).
 */

export type Transaction = "location" | "vente";

export function isForRent(t: string | null | undefined): boolean {
  return t === "location";
}

export function isForSale(t: string | null | undefined): boolean {
  return t === "vente";
}

/**
 * Bien hors marché : `disponibilite` LOUÉ ou VENDU. Un bien hors marché n'a
 * plus de mission ELS/PEB active — son cycle de suivi est terminé (et même
 * réinitialisé au passage en loué, cf. `resetElsCycleForLoue`). Le prestataire
 * ELS/PEB reste assigné (`expertPeb`/`prestataireEle` conservés pour amorcer le
 * prochain cycle), donc ce garde-fou est indispensable pour ne pas laisser un
 * bien loué/vendu traîner dans les worklists prestataires.
 */
export function isOffMarket(disponibilite: string | null | undefined): boolean {
  const d = (disponibilite || "").toUpperCase();
  return d === "LOUÉ" || d === "VENDU";
}

/**
 * Un bien est éligible à une mission prestataire (ELS ou PEB) s'il est dans le
 * circuit location ET encore sur le marché. Source unique partagée par les
 * listes prestataire ELS (`EtatDesLieuxPage`) et PEB (`AuditPebPage`) — il
 * reste à filtrer sur le champ d'assignation propre à chaque liste
 * (`prestataireEle` / `expertPeb`).
 */
export function isMissionEligible(
  bien: { transaction?: string | null; disponibilite?: string | null }
): boolean {
  return isForRent(bien.transaction) && !isOffMarket(bien.disponibilite);
}
